<template>
  <v-sheet
    v-if="!displayConfig.isKiosk"
    class="pa-2 pt-7"
    :width="'100%'"
    :min-width="350"
    :max-width="550"
    :elevation="4"
  >
    <v-form @submit.prevent="loginWithPassword()" validate-on="lazy" v-model="valid">
      <v-container>
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="emailOrUsername"
              label="Username (identikey)"
              :rules="usernameRules"
              density="compact"
              variant="outlined"
              hide-details="auto"
              autocomplete="username"
              required
              clearable
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="password"
              label="Password"
              type="password"
              :rules="passwordRules"
              density="compact"
              variant="outlined"
              hide-details="auto"
              autocomplete="current-password"
              required
              clearable
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-btn type="submit" color="primary">Log in</v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-alert :icon="false" v-if="alert" type="error">{{ alert }}</v-alert>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </v-sheet>
  <StudentIdentifier
    v-else
    :button-text="'Log in'"
    :sign-in-methods="[
      StudentIdentificationMethod.BOC,
      StudentIdentificationMethod.IdentikeyAndSID
    ]"
    @b-o-c="loginWithBOC"
    @identikey-and-s-i-d="loginWithIdentikeyAndSID"
  >
    <template #description>
      <h1>Select your login method</h1>
      <p>
        You may choose to either enter your Student ID and Identikey via a keypad, or tap / swipe
        your Buff OneCard beside the kiosk.
      </p>
    </template>
  </StudentIdentifier>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import { useLoggedInUserStore } from '@/stores/loggedInUser'
import { useDisplayConfigStore } from '@/stores/displayConfig'
import StudentIdentifier from './kiosk/StudentIdentifier.vue'
import { StudentIdentificationMethod } from '@/types/kioskTypes'

export default defineComponent({
  name: 'Login',
  components: { StudentIdentifier },
  data() {
    return {
      valid: false,
      emailOrUsername: '',
      password: '',
      usernameRules: [(v: string) => !!v || 'Email or username is required'],
      passwordRules: [(v: string) => !!v || 'Password is required'],
      alert: ''
    }
  },
  emits: ['close'],
  props: {
    redirectFrom: String
  },
  methods: {
    async loginWithPassword() {
      const userStore = useLoggedInUserStore()

      const user = this.emailOrUsername
      const password = this.password

      await userStore.loginWithUsernamePassword(user, password, this.redirectFrom, this.$router)

      if (userStore.isLoggedIn) {
        this.alert = ''
        this.$emit('close')
      } else {
        this.alert = 'Could not sign in. Please try again.'
      }
    },
    async loginWithIdentikeyAndSID(kioskState: Record<string, string>) {
      if (
        !kioskState[StudentIdentificationMethod.Identikey] ||
        !kioskState[StudentIdentificationMethod.SID]
      )
        return

      const userStore = useLoggedInUserStore()
      await userStore.loginWithIdentikeyAndSID(
        kioskState[StudentIdentificationMethod.Identikey],
        kioskState[StudentIdentificationMethod.SID],
        this.$router,
        this.redirectFrom
      )

      if (userStore.isLoggedIn) {
        this.alert = ''
        this.$emit('close')
      } else {
        this.alert = 'Could not sign in. Please try again.'
      }
    },
    async loginWithBOC(kioskState: Record<string, string>) {
      if (!kioskState[StudentIdentificationMethod.BOC]) return

      const userStore = useLoggedInUserStore()
      await userStore.loginWithBOC(
        kioskState[StudentIdentificationMethod.BOC],
        this.$router,
        this.redirectFrom
      )

      if (userStore.isLoggedIn) {
        this.alert = ''
        this.$emit('close')
      } else {
        this.alert = 'Could not sign in. Please try again.'
      }
    }
  },
  setup() {
    const displayConfig = useDisplayConfigStore()

    return {
      displayConfig,
      StudentIdentificationMethod
    }
  }
})
</script>
